import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Zap, LogOut, User, ChevronRight, CreditCard, RefreshCw } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { UsageIndicatorClient } from '@/components/usage-indicator';
import { AlertsBell } from '@/components/alerts-bell';
import { useExecutionStore } from '@/lib/storage/provider';

const navItems = [
  { href: '/', label: 'Runs' },
  { href: '/projects', label: 'Apps' },
  { href: '/team', label: 'Team' },
];

export function DashboardNav({
  user,
  orgName,
}: {
  user: { id: string; email: string; name?: string };
  orgName?: string | null;
}): import('react/jsx-runtime').JSX.Element {
  const { pathname } = useLocation();

  const initials = (user.email ?? 'U').split('@')[0]!.slice(0, 2).toUpperCase();

  const { clearAll, isSyncing, coveragePct, lastSyncAt } = useExecutionStore();

  const syncTitle = isSyncing
    ? 'Syncing…'
    : lastSyncAt != null
      ? `Last synced ${new Date(lastSyncAt).toLocaleTimeString()} · ${coveragePct}% coverage`
      : 'Not yet synced';

  function handleSignOut(): void {
    clearAll();
    setTimeout((): void => {
      window.location.href = '/api/auth/logout';
    }, 100);
  }

  return (
    <header className="sticky top-0 z-50 border-b bg-glass border-white/5">
      <div className="flex h-11 items-center gap-5 px-4">
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <Zap className="h-4 w-4 text-[var(--flux-accent)]" />
          <span className="text-sm tracking-tight">fluxRun</span>
        </Link>

        {orgName != null && (
          <>
            <ChevronRight className="h-3 w-3 text-muted-foreground/30 shrink-0" />
            <Link
              to="/profile"
              className="text-xs text-muted-foreground truncate max-w-[120px] hover:text-foreground transition-colors"
            >
              {orgName}
            </Link>
          </>
        )}

        <nav className="flex items-center gap-0.5 ml-1">
          {navItems.map((item): import('react/jsx-runtime').JSX.Element => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs transition-colors',
                pathname === item.href
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <span
            title={syncTitle}
            className={cn(
              'flex items-center gap-1 text-xs tabular-nums',
              isSyncing ? 'text-[var(--flux-accent)]' : 'text-muted-foreground/60',
            )}
          >
            <RefreshCw className={cn('h-3 w-3 shrink-0', isSyncing && 'animate-spin')} />
            {isSyncing ? 'Syncing' : `${coveragePct}%`}
          </span>
          <UsageIndicatorClient />
          <AlertsBell />
          <DropdownMenu>
            <DropdownMenuTrigger className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="text-xs">{initials}</AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-xs text-muted-foreground" disabled>
                {user.email}
              </DropdownMenuItem>
              <DropdownMenuItem render={<Link to="/profile" />}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem render={<Link to="/subscription" />}>
                <CreditCard className="mr-2 h-4 w-4" />
                Subscription
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
